import React, { useContext, useEffect, useRef, useState } from "react";
import { GreenMachineContext } from "../GreenMachineContext";
import "./LineupConfiguration.css";
import { commitMutation } from "react-relay";
import relayEnvironment from "../../../../../RelayEnvironment";
import { COMMIT_CONFIG_CHANGES } from "./mutations/CommitLineupConfiguration";

export default function LineupConfiguration() {
  const {
    greenMachineSessionID,
    numLineups,
    setNumLineups,
    minSalary,
    setMinSalary,
    maxSalary,
    setMaxSalary,
    currentStage,
    requestNavigation,
    registerStepNavGuard,
    clearStepNavGuard,
    isNavigating,
    STAGE_NUMBERS,
  } = useContext(GreenMachineContext);

  const STAGE_NUMBER = STAGE_NUMBERS.lineupConfiguration;

  const [validationError, setValidationError] = useState(undefined);

  // Persist + validate on the way out (Continue or a chevron jump). Held in a
  // ref refreshed each render so the registered guard always sees latest state.
  const saveRef = useRef();
  saveRef.current = () =>
    new Promise((resolve) => {
      const minSalaryInt = parseInt(minSalary, 10);
      const maxSalaryInt = parseInt(maxSalary, 10);

      if (isNaN(minSalaryInt) || isNaN(maxSalaryInt)) {
        setValidationError("Please enter valid minimum and maximum salaries.");
        resolve(false);
        return;
      }

      if (minSalaryInt >= maxSalaryInt) {
        setValidationError("Max salary must be greater than min salary.");
        resolve(false);
        return;
      }
      if (
        minSalaryInt > 50000 ||
        minSalaryInt < 0 ||
        maxSalaryInt > 50000 ||
        maxSalaryInt < 0
      ) {
        setValidationError("Min and max salaries must be between 0 and 50000.");
        resolve(false);
        return;
      }

      setValidationError(undefined);

      commitMutation(relayEnvironment, {
        mutation: COMMIT_CONFIG_CHANGES,
        variables: {
          input: {
            greenMachineSessionId: greenMachineSessionID,
            configuration: {
              numLineups: Number.parseInt(numLineups),
              maxSalary: Number.parseInt(maxSalary),
              minSalary: Number.parseInt(minSalary),
            },
          },
        },
        onCompleted: (content, errors) => {
          if (!errors) {
            resolve(true);
          } else {
            setValidationError(errors[0].message);
            resolve(false);
          }
        },
        onError: (error) => {
          console.log(error);
          setValidationError("Something went wrong saving your changes.");
          resolve(false);
        },
      });
    });

  useEffect(() => {
    if (currentStage !== STAGE_NUMBER) {
      return undefined;
    }
    registerStepNavGuard({ saveAndValidate: () => saveRef.current() });
    return () => clearStepNavGuard();
  }, [currentStage, STAGE_NUMBER, registerStepNavGuard, clearStepNavGuard]);

  if (currentStage !== STAGE_NUMBER) {
    return null;
  }

  function rangeSlide(e) {
    e.preventDefault();
    if (e.target.value !== numLineups) {
      setNumLineups(e.target.value);
    }
  }

  const salaryErrorClass = validationError ? " gm-num-input--error" : "";

  return (
    <div className="lineup-configuration-container">
      <div className="gm-step-actions">
        <button
          type="button"
          className="gm-btn-primary"
          disabled={isNavigating}
          onClick={() => requestNavigation(STAGE_NUMBERS.selectPlayers)}
        >
          Continue
          <svg className="gm-cta-arrow" viewBox="0 0 24 24">
            <path d="M8 5l8 7-8 7V5z" />
          </svg>
        </button>
      </div>

      {validationError ? (
        <div className="gm-inline-error">
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path d="M12 2 1 21h22L12 2zm0 5 7 12H5l7-12zm-1 4v3h2v-3h-2zm0 4v2h2v-2h-2z" />
          </svg>
          {validationError}
        </div>
      ) : null}

      <h2>Number of Lineups</h2>
      <span id="lineup-configuration-range-value">{numLineups}</span>
      <input
        type="range"
        value={numLineups}
        step="1"
        min="0"
        max="150"
        onChange={rangeSlide}
      />

      <div className="lineup-config-salary-row">
        <div className="lineup-config-field">
          <label htmlFor="gm-max-salary">Max Salary</label>
          <input
            id="gm-max-salary"
            className={"gm-num-input" + salaryErrorClass}
            type="number"
            max={50000}
            min={0}
            value={maxSalary}
            onChange={(e) => {
              e.preventDefault();
              setMaxSalary(e.target.value);
            }}
          />
        </div>
        <div className="lineup-config-field">
          <label htmlFor="gm-min-salary">Min Salary</label>
          <input
            id="gm-min-salary"
            className={"gm-num-input" + salaryErrorClass}
            type="number"
            max={50000}
            min={0}
            value={minSalary}
            onChange={(e) => {
              e.preventDefault();
              setMinSalary(e.target.value);
            }}
          />
        </div>
      </div>
    </div>
  );
}
